import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, isRef as _isRef, vModelText as _vModelText, withKeys as _withKeys, withModifiers as _withModifiers, createElementVNode as _createElementVNode, withDirectives as _withDirectives, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-a3f3d79c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "relative" }
const _hoisted_2 = ["placeholder", "onKeydown"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("kbd", { class: "hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-stone-400 dark:text-stone-500 border border-stone-300 dark:border-stone-600 rounded" }, " Ctrl K ", -1))
const _hoisted_4 = {
  key: 0,
  class: "absolute top-full left-0 right-0 mt-1 z-[60] rounded-lg border border-stone-300 dark:border-amber-500/30 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md shadow-xl overflow-hidden"
}
const _hoisted_5 = {
  key: 0,
  class: "px-4 py-3 text-sm text-stone-500 dark:text-stone-400"
}
const _hoisted_6 = {
  key: 1,
  class: "px-4 py-3 text-sm text-stone-500 dark:text-stone-400"
}
const _hoisted_7 = {
  key: 2,
  class: "max-h-72 overflow-y-auto py-1"
}
const _hoisted_8 = ["onMouseenter", "onClick"]
const _hoisted_9 = { class: "min-w-0" }
const _hoisted_10 = { class: "text-sm font-medium text-stone-800 dark:text-stone-100 truncate" }
const _hoisted_11 = { class: "text-xs text-stone-500 dark:text-stone-400 truncate" }

import { ref, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";
import { useSearchIndex, type SearchResult } from "@/composables/useSearchIndex";


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalSearch',
  setup(__props) {

const router = useRouter();
const { t } = useI18n();
const { query, results, loading, ready, initSearchIndex } = useSearchIndex();

const open = ref(false);
const highlightedIndex = ref(0);
const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const categoryColor = (category: SearchResult["category"]): string => {
  const map: Record<SearchResult["category"], string> = {
    Page: "bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300",
    Profile: "bg-amber-200/80 dark:bg-amber-500/30 text-amber-800 dark:text-amber-200",
    Blog: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
    Project: "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300",
    Resume: "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300",
  };
  return map[category];
};

const navigateTo = (result: SearchResult): void => {
  const path = result.anchor ? `${result.route}#${result.anchor}` : result.route;
  router.push(path);
  closeSearch();
};

const closeSearch = (): void => {
  open.value = false;
  query.value = "";
  highlightedIndex.value = 0;
};

const moveHighlight = (delta: number): void => {
  if (!results.value.length) return;
  highlightedIndex.value =
    (highlightedIndex.value + delta + results.value.length) % results.value.length;
};

const selectHighlighted = (): void => {
  const result = results.value[highlightedIndex.value];
  if (result) navigateTo(result);
};

const handleClickOutside = (e: MouseEvent): void => {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) {
    open.value = false;
  }
};

const handleGlobalKey = (e: KeyboardEvent): void => {
  if ((e.ctrlKey || e.metaKey) && e.key === "k") {
    e.preventDefault();
    inputRef.value?.focus();
    open.value = true;
  }
};

watch(query, () => {
  highlightedIndex.value = 0;
  if (query.value.trim()) open.value = true;
});

onMounted(() => {
  initSearchIndex();
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleGlobalKey);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleGlobalKey);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: "relative w-full sm:w-64 md:w-80 max-w-md"
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Icon), {
        icon: "mdi:magnify",
        class: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none"
      }),
      _withDirectives(_createElementVNode("input", {
        ref_key: "inputRef",
        ref: inputRef,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(query) ? (query).value = $event : null)),
        type: "search",
        placeholder: _unref(t)('search.placeholder'),
        class: "w-full h-9 pl-9 pr-16 text-sm rounded-lg border border-stone-300 dark:border-amber-500/40 bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm text-stone-800 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60 dark:focus:ring-amber-500/50",
        onFocus: _cache[1] || (_cache[1] = ($event: any) => (open.value = true)),
        onKeydown: [
          _withKeys(closeSearch, ["escape"]),
          _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (moveHighlight(1)), ["prevent"]), ["down"])),
          _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event: any) => (moveHighlight(-1)), ["prevent"]), ["up"])),
          _withKeys(_withModifiers(selectHighlighted, ["prevent"]), ["enter"])
        ]
      }, null, 40, _hoisted_2), [
        [_vModelText, _unref(query)]
      ]),
      _hoisted_3
    ]),
    _createVNode(_Transition, { name: "search-drop" }, {
      default: _withCtx(() => [
        (open.value && (_unref(query).trim() || _unref(loading)))
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (_unref(loading) && !_unref(ready))
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)("search.loading")), 1))
                : (_unref(query).trim() && _unref(results).length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)("search.noResults", { query: _unref(query) })), 1))
                  : (_unref(results).length)
                    ? (_openBlock(), _createElementBlock("ul", _hoisted_7, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(results), (result, index) => {
                          return (_openBlock(), _createElementBlock("li", {
                            key: result.id,
                            onMouseenter: ($event: any) => (highlightedIndex.value = index),
                            onClick: ($event: any) => (navigateTo(result)),
                            class: _normalizeClass([
              'px-3 py-2 cursor-pointer flex items-start gap-3 transition-colors',
              highlightedIndex.value === index
                ? 'bg-amber-100/80 dark:bg-amber-500/20'
                : 'hover:bg-stone-100 dark:hover:bg-stone-800/60',
            ])
                          }, [
                            _createElementVNode("span", {
                              class: _normalizeClass([categoryColor(result.category), "mt-0.5 shrink-0 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded"])
                            }, _toDisplayString(_unref(t)(`search.categories.${result.category}`)), 3),
                            _createElementVNode("div", _hoisted_9, [
                              _createElementVNode("p", _hoisted_10, _toDisplayString(result.title), 1),
                              _createElementVNode("p", _hoisted_11, _toDisplayString(result.excerpt), 1)
                            ])
                          ], 42, _hoisted_8))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 512))
}
}

})